import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';

import { saveCustomReport } from 'api/Reports';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import Button from 'components/shared/Buttons/Button/Button';
import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Switch from 'components/shared/Switch/Switch';
import Typography from 'components/shared/Typography/Typography';

const SaveCustomReportModal = ({ reportId, name = '', filters, customReport }) => {
  const navigate = useNavigate();
  const { isOpen, openModal, closeModal } = useModal();

  const formik = useFormik({
    initialValues: {
      name: name + ' - copy',
      team_visibility: false
    }
  });

  const isTeamVisibility = formik.values.team_visibility;
  const queryClient = useQueryClient();

  const onModalOpen = () => openModal();

  const onChangeReportName = (e) => formik.setFieldValue('name', e.target.value);
  const onChangeShareWith = (value) => formik.setFieldValue('team_visibility', value);

  const { mutateAsync: saveCustomReportHandler, isLoading } = useCustomMutation({
    mutationFn: (params) => saveCustomReport(navigate, params),
    successMessage: 'Report saved successfully',
    errorMessage: 'Failed to save report. Please try again.',
    refetchQueryKey: ['custom-reports'],
    onSuccess: () => {
      formik.resetForm();
      closeModal();
    }
  });

  const onSaveHandler = async () => {
    await saveCustomReportHandler({
      name: formik.values.name,
      team_visibility: formik.values.team_visibility,
      filters: Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [
          key,
          value.map((filter) => ({ values: filter.values, formula: filter.formula }))
        ])
      ),
      custom_ui: customReport,
      report_id: reportId
    });
    queryClient.invalidateQueries({ queryKey: ['custom-reports'] });
  };

  return (
    <>
      <Button text="Save as Custom Report" size="xs" style="outlined" onClick={onModalOpen} />
      {isOpen && (
        <Modal
          title="Save Custom Report"
          isOpen={isOpen}
          handleClose={closeModal}
          bodyClassName="h-fit gap-4 flex flex-col"
          footer={
            <div className="flex w-full justify-between gap-2">
              <Button text="Cancel" onClick={closeModal} outlined color="neutral" />
              <Button text="Save" onClick={onSaveHandler} loading={isLoading} />
            </div>
          }>
          <Input label="Report Name" value={formik.values.name} onChange={onChangeReportName} />

          <div className="flex flex-col gap-2">
            <Typography>Share with others</Typography>

            <div className="flex items-center gap-2">
              <Switch checked={isTeamVisibility} onChange={onChangeShareWith} />
              <Typography size="small" color="neutral-500">
                {isTeamVisibility
                  ? 'Visible to anyone with access to report.'
                  : 'This report is only visible to you.'}
              </Typography>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SaveCustomReportModal;
